import { Box } from '@mui/material';

import { AISuggestionsMatcher } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher';
import { AISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { AISuggestionsMatcherProvider } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/AISuggestionsMatcher.provider';

import { useBeneficiaryDesignationSuggestionsContext } from './contexts/beneficiaryDesignationSuggestions.context';

const TIER_VARIANT: Record<
  'primary' | 'contingent',
  AISuggestionsMatcherVariant
> = {
  primary:
    AISuggestionsMatcherVariant.BENEFICIARY_DESIGNATION_RECIPIENTS_PRIMARY,
  contingent:
    AISuggestionsMatcherVariant.BENEFICIARY_DESIGNATION_RECIPIENTS_CONTINGENT,
};

interface BeneficiaryDesignationSuggestionsMatcherProps {
  tier: 'primary' | 'contingent';
}

/**
 * The "✨ Confirm recipients" card for one beneficiary-designation tier. Reuses
 * the shared `AISuggestionsMatcher`; on confirm the matcher appends mapped
 * recipients to `beneficiaryDesignations.{tier}.recipients` (persisted by the
 * BDG mutation on save) and on ignore acknowledges the suggestion as rejected.
 *
 * Suggestions are fetched once by `BeneficiaryDesignationSuggestionsProvider` at
 * the form root and read here per tier. Each tier still gets its own
 * `AISuggestionsMatcherProvider` because the provider tracks a single matcher
 * variant (and its matched-suggestion state) at a time.
 */
export function BeneficiaryDesignationSuggestionsMatcher(
  props: BeneficiaryDesignationSuggestionsMatcherProps
) {
  return (
    <AISuggestionsMatcherProvider>
      <BeneficiaryDesignationSuggestionsMatcherInner {...props} />
    </AISuggestionsMatcherProvider>
  );
}

function BeneficiaryDesignationSuggestionsMatcherInner({
  tier,
}: BeneficiaryDesignationSuggestionsMatcherProps) {
  const { primary, contingent } = useBeneficiaryDesignationSuggestionsContext();
  const suggestions = tier === 'primary' ? primary : contingent;

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <Box px={2}>
      <AISuggestionsMatcher
        variant={TIER_VARIANT[tier]}
        suggestions={suggestions}
      />
    </Box>
  );
}
